import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2 } from "lucide-react";
import { mutate } from "swr";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { FormDialog } from "~/pages/manifold-nextgen/components/FormDialog";
import { useAsyncMutation } from "~/pages/manifold-nextgen/hooks/useAsyncMutation";
import { nextgenProjectsListPath } from "~/pages/manifold-nextgen/paths";
import {
  Project,
  deleteProject,
  projectListPath,
  projectPath,
  updateProject,
} from "~/services/projects";

interface ProjectSettingsDialogProps {
  project: Project;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function formatTimestamp(isoDate: string): string {
  return new Date(isoDate).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export const ProjectSettingsDialog = ({
  project,
  open,
  onOpenChange,
}: ProjectSettingsDialogProps) => {
  const navigate = useNavigate();
  const { showSuccess } = useToast();

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description ?? "");
  const [deleteOpen, setDeleteOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setName(project.name);
      setDescription(project.description ?? "");
    }
  }, [open, project.name, project.description]);

  const hasChanges = name !== project.name || description !== (project.description ?? "");

  const { execute: save, isPending: isSaving } = useAsyncMutation(
    (input: { name: string; description: string }) => updateProject(project.short_id, input),
    {
      errorMessage: "Failed to save project",
      onSuccess: async () => {
        await Promise.all([mutate(projectPath(project.short_id)), mutate(projectListPath())]);
        showSuccess("Project updated");
        onOpenChange(false);
      },
    }
  );

  const { execute: remove, isPending: isDeleting } = useAsyncMutation(
    () => deleteProject(project.short_id),
    {
      errorMessage: "Failed to delete project",
      onSuccess: async () => {
        await mutate(projectListPath());
        showSuccess(`"${project.name}" deleted`);
        navigate(nextgenProjectsListPath());
      },
      onError: () => setDeleteOpen(false),
    }
  );

  const handleSave = (e: FormEvent) => {
    e.preventDefault();
    if (!hasChanges) return;
    void save({ name: name.trim(), description: description.trim() });
  };

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title="Project settings"
        description="Update name, description, or delete this project."
        contentClassName="sm:max-w-[560px]"
        onSubmit={handleSave}
        isPending={isSaving}
        primaryLabel="Save changes"
        primaryPendingLabel="Saving…"
        primaryDisabled={!hasChanges || !name.trim()}
      >
        <div className="flex flex-col gap-5 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="project-settings-name">Name</Label>
            <Input
              id="project-settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={500}
              required
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="project-settings-description">Description</Label>
            <Textarea
              id="project-settings-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Markdown supported."
              rows={6}
            />
          </div>

          <dl className="grid grid-cols-1 gap-3 border-t pt-4 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-muted-foreground">Owner</dt>
              <dd className="text-foreground">{project.owner_user_id}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Project ID</dt>
              <dd className="font-mono text-xs text-foreground">{project.short_id}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Created</dt>
              <dd className="text-foreground">{formatTimestamp(project.created_at)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Updated</dt>
              <dd className="text-foreground">{formatTimestamp(project.updated_at)}</dd>
            </div>
          </dl>

          <div className="flex items-center justify-between gap-4 rounded-md border border-destructive/40 p-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Delete project</p>
              <p className="text-xs text-muted-foreground">
                Soft-deletes the project. Attached conversations, agents, and datasets are
                preserved.
              </p>
            </div>
            <Button
              type="button"
              variant="destructive"
              onClick={() => setDeleteOpen(true)}
              disabled={isDeleting}
            >
              <Trash2 aria-hidden="true" />
              Delete
            </Button>
          </div>
        </div>
      </FormDialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{project.name}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              The project will be soft-deleted and hidden from the list. Conversations, agents, and
              datasets attached to it remain intact.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button variant="destructive" onClick={() => void remove()} disabled={isDeleting}>
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
